import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Search as SearchIcon } from "@mui/icons-material";
import { getConvertsScannerLatestDateData } from "../../../services/Axios";
import BondDetailsModal from "./BondDetailsModal";

const SummaryTab = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBond, setSelectedBond] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchLatestDateData();
  }, []);

  const fetchLatestDateData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getConvertsScannerLatestDateData();
      if (response?.data) {
        setData(response.data);
        setLatestDate(response.latest_date);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Error loading data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = Number(value);
    if (isNaN(numValue)) return "N/A";
    return numValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatPtsChange = (value, compact = true) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = Number(value);
    if (isNaN(numValue)) return "N/A";
    const formatted = numValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (
      <span
        style={{
          color: numValue < 0 ? theme.palette.error.main : theme.palette.text.primary,
          fontWeight: numValue < 0 ? 600 : 400,
          fontSize: compact ? "0.7rem" : "0.875rem",
        }}
      >
        {numValue > 0 ? `+${formatted}` : formatted}
      </span>
    );
  };

  // Process data to get top movers for each time period
  const topMovers = useMemo(() => {
    if (!data || data.length === 0) return {};

    const periods = [
      { key: "1d", field: "pts_change_from_1d_ago", label: "1 Day" },
      { key: "1w", field: "pts_change_from_1w_ago", label: "1 Week" },
      { key: "2w", field: "pts_change_from_2w_ago", label: "2 Weeks" },
      { key: "3w", field: "pts_change_from_3w_ago", label: "3 Weeks" },
      { key: "1m", field: "pts_change_from_1m_ago", label: "1 Month" },
    ];

    const result = {};

    periods.forEach((period) => {
      // Filter out null/undefined values and sort
      // Include zero values but exclude null/undefined/empty/NaN
      const validData = data
        .map((item) => {
          const value = item[period.field];
          // Handle various data types: string, number, null, undefined
          if (value === null || value === undefined || value === "") {
            return null; // Mark for filtering
          }
          
          // Convert to number - handle string decimals from Django DecimalField
          const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
          
          if (isNaN(numValue)) {
            return null; // Mark for filtering
          }
          
          return {
            name: item.name || "N/A",
            ptsChange: numValue,
            outstandingAmt: Number(item.outstanding_amt_mm) || 0,
          };
        })
        .filter((item) => item !== null); // Remove null items

      // Sort by pts change: highest first, then lowest first
      const sortedByHighest = [...validData].sort((a, b) => b.ptsChange - a.ptsChange);
      const sortedByLowest = [...validData].sort((a, b) => a.ptsChange - b.ptsChange);

      result[period.key] = {
        label: period.label,
        highest: sortedByHighest.slice(0, 10),
        lowest: sortedByLowest.slice(0, 10),
      };
    });

    return result;
  }, [data]);

  const handleExpand = (bondName) => {
    // Find the full bond data from the data array
    const bondData = data.find((item) => item.name === bondName);
    if (bondData) {
      setSelectedBond(bondData);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBond(null);
  };

  const TopMoversTable = ({ title, rows, compact = true }) => {
    if (!rows || rows.length === 0) {
      return (
        <Paper 
          elevation={1} 
          sx={{ 
            p: compact ? 1 : 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography 
            variant={compact ? "subtitle2" : "h6"} 
            sx={{ 
              mb: compact ? 0.5 : 2, 
              fontWeight: 600,
              fontSize: compact ? "0.75rem" : "1rem",
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}
          >
            No data available
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: compact ? 1 : 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography 
          variant={compact ? "subtitle2" : "h6"} 
          sx={{ 
            mb: compact ? 0.5 : 2, 
            fontWeight: 600,
            fontSize: compact ? "0.75rem" : "1rem",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <TableContainer sx={{ flex: 1, maxHeight: compact ? "300px" : "none" }}>
          <Table 
            size="small" 
            sx={{ 
              tableLayout: "fixed",
              width: "100%",
              "& .MuiTableCell-root": { padding: compact ? "4px 8px" : "8px" } 
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: theme.palette.primary.dark, 
                    color: theme.palette.primary.contrastText,
                    fontSize: compact ? "0.7rem" : "0.875rem",
                    padding: compact ? "6px 8px" : "8px",
                    width: 40,
                    minWidth: 40,
                    maxWidth: 40,
                  }}
                >
                  
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: theme.palette.primary.dark, 
                    color: theme.palette.primary.contrastText,
                    fontSize: compact ? "0.7rem" : "0.875rem",
                    padding: compact ? "6px 8px" : "8px",
                  }}
                >
                  Name
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: theme.palette.primary.dark, 
                    color: theme.palette.primary.contrastText,
                    fontSize: compact ? "0.7rem" : "0.875rem",
                    padding: compact ? "6px 8px" : "8px",
                    width: 60,
                    minWidth: 60,
                    maxWidth: 60,
                  }}
                >
                  Pts Chg
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontWeight: 600, 
                    backgroundColor: theme.palette.primary.dark, 
                    color: theme.palette.primary.contrastText,
                    fontSize: compact ? "0.7rem" : "0.875rem",
                    padding: compact ? "6px 8px" : "8px",
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                  }}
                >
                  Out ($mm)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "& .MuiTableCell-root": {
                      fontSize: compact ? "0.7rem" : "0.875rem",
                      padding: compact ? "4px 8px" : "8px",
                    },
                  }}
                >
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontSize: compact ? "0.7rem" : "0.875rem",
                      width: 40,
                      minWidth: 40,
                      maxWidth: 40,
                      padding: compact ? "4px 4px" : "4px",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleExpand(row.name)}
                      sx={{
                        padding: 0.25,
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontSize: compact ? "0.7rem" : "0.875rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                    }}
                  >
                    {row.name}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontSize: compact ? "0.7rem" : "0.875rem",
                      width: 60,
                      minWidth: 60,
                      maxWidth: 60,
                    }}
                  >
                    {formatPtsChange(row.ptsChange, compact)}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontSize: compact ? "0.7rem" : "0.875rem",
                      "& span": { fontSize: compact ? "0.7rem" : "0.875rem" },
                      width: 60,
                      minWidth: 60,
                      maxWidth: 60,
                    }}
                  >
                    <span>{formatNumber(row.outstandingAmt, 0)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          boxShadow: theme.shadows[1],
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const periods = [
    { key: "1d", label: "1d" },
    { key: "1w", label: "1w" },
    { key: "2w", label: "2w" },
    { key: "3w", label: "3w" },
    { key: "1m", label: "1m" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Top Movers Summary
      </Typography>
      {latestDate && (
        <Typography variant="caption" sx={{ mb: 2, color: theme.palette.text.secondary, display: "block" }}>
          As of: {latestDate} ({data.length} records)
        </Typography>
      )}

      {/* Top Movers Section - Highest */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.success.main }}>
          Top Movers (Highest)
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            "& > *": {
              flex: "1 1 calc(20% - 12px)",
              minWidth: "200px",
              maxWidth: "calc(20% - 12px)",
            },
          }}
        >
          {periods.map((period) => {
            const periodData = topMovers[period.key];
            if (!periodData) return null;
            return (
              <TopMoversTable
                key={`highest-${period.key}`}
                title={`Top 10 (${period.label})`}
                rows={periodData.highest}
                compact={true}
              />
            );
          })}
        </Box>
      </Box>

      {/* Top Laggers Section - Lowest */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.error.main }}>
          Top Laggers (Lowest)
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            "& > *": {
              flex: "1 1 calc(20% - 12px)",
              minWidth: "200px",
              maxWidth: "calc(20% - 12px)",
            },
          }}
        >
          {periods.map((period) => {
            const periodData = topMovers[period.key];
            if (!periodData) return null;
            return (
              <TopMoversTable
                key={`lowest-${period.key}`}
                title={`Top 10 (${period.label})`}
                rows={periodData.lowest}
                compact={true}
              />
            );
          })}
        </Box>
      </Box>
      <BondDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        bondData={selectedBond}
      />
    </Box>
  );
};

export default SummaryTab;
